import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@ouropix/ui';
import { Textarea } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Shield,
  ArrowUpRight,
  User,
} from 'lucide-react';

export const Route = createFileRoute('/_admin/saques/manual-review')({
  component: ManualReviewPage,
});

interface WithdrawalItem {
  id: string;
  user_id: string;
  user_masked: string;
  amount: string;
  pix_key: string;
  pix_key_type: string;
  status: string;
  requires_manual_review: boolean;
  review_reason: string;
  device_risk_score: number;
  created_at: string;
  updated_at: string;
  // PII fields (admin-visible)
  holder_name?: string;
  holder_document?: string;
  holder_bank?: string;
}

function mapApiWithdrawal(w: any): WithdrawalItem {
  return {
    id: w.id,
    user_id: w.userId ?? w.user_id ?? '',
    user_masked: w.userMasked ?? w.user_masked ?? '***',
    amount: w.amount ?? '0',
    pix_key: w.pixKey ?? w.pix_key ?? '',
    pix_key_type: w.pixKeyType ?? w.pix_key_type ?? '',
    status: w.status ?? 'manual_review',
    requires_manual_review: true,
    review_reason: w.reviewReason ?? w.review_reason ?? '',
    device_risk_score: Number(w.deviceRiskScore ?? w.device_risk_score ?? 0),
    created_at: w.createdAt ?? w.created_at ?? '',
    updated_at: w.updatedAt ?? w.updated_at ?? '',
    holder_name: w.holderName ?? w.holder_name,
    holder_document: w.holderDocument ?? w.holder_document,
    holder_bank: w.holderBank ?? w.holder_bank,
  };
}

function maskPixKey(key: string): string {
  if (!key) return '--';
  if (key.length <= 6) return key;
  return key.slice(0, 3) + '***' + key.slice(-3);
}

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
}

function formatDate(iso: string): string {
  if (!iso) return '--';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const PAGE_SIZE = 20;

function ManualReviewPage() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [approveId, setApproveId] = useState<string | null>(null);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [escalateId, setEscalateId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [escalateNote, setEscalateNote] = useState('');
  const [detailItem, setDetailItem] = useState<WithdrawalItem | null>(null);

  // TODO: refactor para /admin/payouts?filter=manual_review quando Yuri entregar endpoint
  const { data, isLoading } = useQuery<{
    withdrawals: WithdrawalItem[];
    total: number;
  }>({
    queryKey: ['admin-manual-review-withdrawals', currentPage],
    queryFn: async () => {
      const res = await api.get('/admin/withdrawals/pending', {
        params: {
          page: currentPage,
          limit: PAGE_SIZE,
          status: 'manual_review',
        },
      });
      const raw = res.data?.withdrawals ?? res.data?.data ?? [];
      const total = res.data?.total ?? res.data?.pagination?.total ?? raw.length ?? 0;
      return {
        withdrawals: (Array.isArray(raw) ? raw : []).map(mapApiWithdrawal),
        total,
      };
    },
  });

  const withdrawals = data?.withdrawals ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const approveMutation = useMutation({
    mutationFn: async (id: string) =>
      api.post(`/admin/withdrawals/${id}/approve`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-manual-review-withdrawals'] });
      toast.success('Saque aprovado! PIX será processado.');
      setApproveId(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Erro ao aprovar saque');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/admin/withdrawals/${id}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-manual-review-withdrawals'] });
      toast.success('Saque rejeitado. Saldo estornado se necessário.');
      setRejectId(null);
      setRejectReason('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Erro ao rejeitar saque');
    },
  });

  const escalateMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) =>
      api.post(`/admin/withdrawals/${id}/escalate`, { note }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-manual-review-withdrawals'] });
      toast.success('Saque escalado para compliance.');
      setEscalateId(null);
      setEscalateNote('');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Erro ao escalar saque');
    },
  });

  const riskColor = (score: number) => {
    if (score > 70) return 'text-destructive font-semibold';
    if (score > 40) return 'text-orange-500';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-orange-500" />
        <div>
          <h1 className="text-2xl font-bold">Revisão Manual de Saques</h1>
          <p className="text-muted-foreground text-sm">
            Saques sinalizados para revisão humana — aprovação, rejeição ou escalonamento
          </p>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titular</TableHead>
              <TableHead>Valor</TableHead>
              <TableHead className="hidden md:table-cell">Chave PIX</TableHead>
              <TableHead className="hidden lg:table-cell">Motivo</TableHead>
              <TableHead className="hidden lg:table-cell">Risco</TableHead>
              <TableHead className="hidden sm:table-cell">Data</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : withdrawals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  Nenhum saque aguardando revisão manual
                </TableCell>
              </TableRow>
            ) : (
              withdrawals.map((w) => (
                <TableRow key={w.id}>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{w.user_masked}</span>
                      {w.holder_name && (
                        <span className="text-xs text-muted-foreground">{w.holder_name}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono font-semibold">
                      {formatCurrency(w.amount)}
                    </span>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground uppercase">
                        {w.pix_key_type || 'pix'}
                      </span>
                      <span className="font-mono text-sm">{maskPixKey(w.pix_key)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {w.review_reason ? (
                      <Badge variant="outline" className="border-orange-500 text-orange-500 gap-1 text-xs">
                        <AlertTriangle className="h-3 w-3" />
                        {w.review_reason.slice(0, 30)}{w.review_reason.length > 30 ? '...' : ''}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">--</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <span className={riskColor(w.device_risk_score)}>
                      {w.device_risk_score}
                    </span>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                    {formatDate(w.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={() => setDetailItem(w)}
                        title="Ver detalhes PII"
                      >
                        <User className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-600 hover:bg-green-50"
                        onClick={() => setApproveId(w.id)}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Aprovar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-destructive border-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setRejectId(w.id);
                          setRejectReason('');
                        }}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Rejeitar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                        onClick={() => {
                          setEscalateId(w.id);
                          setEscalateNote('');
                        }}
                      >
                        <ArrowUpRight className="h-3 w-3 mr-1" />
                        Escalar
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {currentPage} de {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">{currentPage} / {totalPages}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* PII detail dialog */}
      <Dialog
        open={!!detailItem}
        onOpenChange={(open) => { if (!open) setDetailItem(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dados do Titular</DialogTitle>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Usuário (masked)</span>
                <span className="font-mono">{detailItem.user_masked}</span>
                <span className="text-muted-foreground">Titular</span>
                <span>{detailItem.holder_name ?? '--'}</span>
                <span className="text-muted-foreground">Documento</span>
                <span className="font-mono">{detailItem.holder_document ?? '--'}</span>
                <span className="text-muted-foreground">Banco</span>
                <span>{detailItem.holder_bank ?? '--'}</span>
                <span className="text-muted-foreground">Chave PIX</span>
                <span className="font-mono">{detailItem.pix_key}</span>
                <span className="text-muted-foreground">Tipo</span>
                <span className="uppercase">{detailItem.pix_key_type}</span>
                <span className="text-muted-foreground">Valor</span>
                <span className="font-semibold">{formatCurrency(detailItem.amount)}</span>
                <span className="text-muted-foreground">Score de risco</span>
                <span className={riskColor(detailItem.device_risk_score)}>
                  {detailItem.device_risk_score}
                </span>
                <span className="text-muted-foreground">Motivo</span>
                <span>{detailItem.review_reason || '--'}</span>
                <span className="text-muted-foreground">Criado em</span>
                <span>{formatDate(detailItem.created_at)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailItem(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve confirmation */}
      <ConfirmModal
        isOpen={!!approveId}
        onClose={() => setApproveId(null)}
        onConfirm={() => { if (approveId) approveMutation.mutate(approveId); }}
        title="Aprovar Saque"
        description="Confirma aprovação deste saque? O PIX será transferido para a chave do titular via EFI."
        confirmText="Aprovar"
        cancelText="Cancelar"
      />

      {/* Reject dialog */}
      <Dialog
        open={!!rejectId}
        onOpenChange={(open) => {
          if (!open) { setRejectId(null); setRejectReason(''); }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rejeitar Saque</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reject-reason">Motivo da rejeição *</Label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Descreva o motivo..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectId(null); setRejectReason(''); }}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || rejectMutation.isPending}
              onClick={() => {
                if (rejectId && rejectReason.trim()) {
                  rejectMutation.mutate({ id: rejectId, reason: rejectReason });
                }
              }}
            >
              {rejectMutation.isPending ? 'Rejeitando...' : 'Rejeitar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate dialog */}
      <Dialog
        open={!!escalateId}
        onOpenChange={(open) => {
          if (!open) { setEscalateId(null); setEscalateNote(''); }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Escalar para Compliance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="escalate-note">Observação *</Label>
              <Textarea
                id="escalate-note"
                value={escalateNote}
                onChange={(e) => setEscalateNote(e.target.value)}
                placeholder="Descreva o motivo do escalonamento..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEscalateId(null); setEscalateNote(''); }}>
              Cancelar
            </Button>
            <Button
              className="bg-yellow-600 hover:bg-yellow-700 text-white"
              disabled={!escalateNote.trim() || escalateMutation.isPending}
              onClick={() => {
                if (escalateId && escalateNote.trim()) {
                  escalateMutation.mutate({ id: escalateId, note: escalateNote });
                }
              }}
            >
              {escalateMutation.isPending ? 'Escalando...' : 'Escalar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
